import { scaleLinear, ScaleLinear } from 'd3-scale'
import { bisector } from 'd3-array'
import { axisBottom } from 'd3-axis'
import { interpolateYlOrBr } from 'd3-scale-chromatic'

import { css } from 'emotion'

import { colorScaleOptions, fillColorScaleLegend } from '../common/colorScale'
import { getProportionalY, interpolate, Interpolation } from '../common/interpolations'
import CanvasChart from '../common/CanvasChart'
import { DashTheme } from '../wbs/types'
import { LiveWidget } from 'live/webapp/types/widget'
import { JsonConfig } from '../../types'

type State = {
    items: any[]
    extent: [number, number]
}

const legendCss = css`
    position: relative;

    svg.imagelog-legend {
        position: absolute;
        width: 100%;
        height: 100px;
    }
`

const canvasLegendCss = css`
    position: absolute;
    width: 100%;
    top: 0px;
    canvas {
        position: relative;
        width: calc(100% - 40px);
    }
`
const FRAME = {
    ZOOM: 80,
    DEFAULT: 40
}
export default class ImagelogChart extends CanvasChart<State> {
    private raf: number
    private dashTheme: DashTheme
    private widgetConfig: JsonConfig

    init(): void {
        super.init()
        this.el.classed('imagelog-chart', true)
    }

    get isVertical(): boolean {
        return true
    }

    getExtents = (
        extent: number[],
        scaleMin: number,
        scaleMax: number
    ): { dataExtent: number[]; scaleExtent: number[] } => {
        const dataExtent = extent ? [extent[0] ?? 0, extent[1] ?? 0] : [0, 0]
        const scaleExtent = [scaleMin ?? dataExtent[0], scaleMax ?? dataExtent[1]]

        return { dataExtent: dataExtent, scaleExtent: scaleExtent }
    }

    onMouseMove(mouse: [number, number], scale: ScaleLinear<number, number>): void {
        const data = this.subject.getValue()
        const x = mouse[this.isVertical ? 1 : 0]
        const value = scale.invert(x)
        const left = bisector((x: { timestamp: number }) => x.timestamp).left
        const { scaleMin, scaleMax, useDefaultMinMax } = this.widgetConfig
        const { dataExtent, scaleExtent } = this.getExtents(data.extent, scaleMin, scaleMax)
        const legendScale = scaleLinear()
            .domain(useDefaultMinMax ? dataExtent : scaleExtent)
            .range([0, this.width - 40])
            .clamp(true)
        const index = left(data.items, value)

        if (index !== undefined) {
            const section = data.items[index - 1]
            if (section) {
                const xIndex = Math.floor((mouse[0] / this.innerWidth) * 16)
                const value = section.log[xIndex]
                if (value) {
                    const legendX = legendScale(value)
                    this._legend
                        .selectAll('line.legend-position')
                        .attr('x1', legendX + 20)
                        .attr('x2', legendX + 20)

                    this._legend.selectAll('text.legend-value').attr('opacity', '1').text(value)
                } else {
                    this._legend.selectAll('text.legend-value').attr('opacity', '0')
                }
            }
        }
    }

    render(data: State, options?: { dashboardTheme: DashTheme; widget: LiveWidget }): void {
        this.dashTheme = options?.dashboardTheme || 'light'
        if (options?.widget) this.widgetConfig = options.widget.jsonConfig
        super.render(data)
    }

    renderLegend(data: State): void {
        const { scaleMin, scaleMax, useDefaultMinMax, colorScaleOption } = this.widgetConfig ?? this.options.cfg
        function buildLegendPattern(sel, scale: (t: number) => string): void {
            const canvas = sel.node()
            fillColorScaleLegend(scale, canvas)
        }
        const { dataExtent, scaleExtent } = this.getExtents(data.extent, scaleMin, scaleMax)

        function buildSvglegend(sel, extent: number[] | undefined): void {
            if (!extent || (!extent[0] && !extent[1])) return
            if (!sel.node()) return
            const bbox = sel.node().getBoundingClientRect()

            const margin = 20
            const height = 20
            sel.selectAll('rect')
                .data([null])
                .join('rect')
                .attr('x', margin)
                .attr('y', margin)
                .attr('width', bbox.width - margin * 2)
                .attr('height', height)
                .attr('stroke', '#333')
                .attr('stroke-width', '1px')
                .attr('fill', 'none')
                .attr('shape-rendering', 'crispedges')

            sel.selectAll('line.legend-position')
                .data([null])
                .join('line')
                .classed('legend-position', true)
                .attr('x1', 100)
                .attr('x2', 100)
                .attr('y1', margin)
                .attr('y2', margin + height)
                .attr('stroke', '#333')
                .attr('shape-rendering', 'crispedges')

            sel.selectAll('text.legend-value')
                .data([null])
                .join('text')
                .classed('legend-value', true)
                .attr('fill', '#333')
                .attr('text-anchor', 'middle')
                .attr('x', bbox.width / 2)
                .attr('y', 15)
                .text('')

            const scale = scaleLinear()
                .domain(extent)
                .range([0, bbox.width - margin * 2])

            const bottomAxis = axisBottom(scale).tickValues([extent[0], ...scale.ticks(2), extent[1]])
            const legendAxisOffset = 0.5
            sel.selectAll('g.legend-axis')
                .data([null])
                .join('g')
                .classed('legend-axis', true)
                .attr('transform', `translate(${margin - legendAxisOffset}, ${margin + height - legendAxisOffset})`)
                .call(bottomAxis)
        }

        this._legend
            .classed(legendCss, true)
            .selectAll('div.imagelog-legend')
            .data([null])
            .join('div')
            .classed('imagelog-legend', true)
            .classed(canvasLegendCss, true)
            .selectAll('canvas')
            .data([null])
            .join('canvas')
            .style('height', '100%')
            .style('top', '20px')
            .style('left', '20px')
            .style('height', `20px`)
            .call(buildLegendPattern, colorScaleOption ? colorScaleOptions[colorScaleOption] : interpolateYlOrBr)

        this._legend
            .selectAll('svg.imagelog-legend')
            .data([null])
            .join('svg')
            .classed('imagelog-legend', true)
            .call(buildSvglegend, useDefaultMinMax ? dataExtent : scaleExtent)
    }

    buildColorScale(data: State): (value: number) => string {
        const { scaleMin, scaleMax, useDefaultMinMax, colorScaleOption } = this.widgetConfig
        const { dataExtent, scaleExtent } = this.getExtents(data.extent, scaleMin, scaleMax)
        const colorScale = scaleLinear()
            .domain(useDefaultMinMax ? dataExtent : scaleExtent)
            .range([1, 0])

        const interpolateFn = colorScaleOption ? colorScaleOptions[colorScaleOption] : interpolateYlOrBr

        return function (value: number): string {
            return interpolateFn(colorScale(value))
        }
    }

    /**
     * Build ImageLog Canvas
     * @param canvas
     * @param data
     * @param scale
     */
    renderCanvas(canvas: HTMLCanvasElement, data: State, scale: ScaleLinear<number, number>): void {
        if (data.items?.length > 0) {
            const scaleColor = this.buildColorScale(data)
            const ctx = canvas?.getContext('2d') as CanvasRenderingContext2D

            const height = canvas?.height
            const width = canvas?.width
            ctx?.clearRect(0, 0, width, height)

            const columns = data.items[0].log.length
            const xScale = scaleLinear().domain([0, columns]).range([0, width])

            const imageData = ctx?.getImageData(0, 0, width, height)

            const image = data.items.map(item => item.log)

            if (this.raf) {
                window.cancelAnimationFrame(this.raf)
            }

            let y = 0
            const renderFrame = (): void => {
                const t0 = performance.now()

                for (; y < height; y++) {
                    const j = scale.invert(y)
                    if (j < data.items[0].timestamp || j > data.items[data.items.length - 1].timestamp) continue

                    const y0 = getProportionalY(data.items, j)

                    for (let x = 0; x < width; x++) {
                        const x0 = xScale.invert(x)

                        const value =
                            Math.abs(Math.round(x0) - x0) < 1e-6 && Math.abs(Math.round(y0) - y0) < 1e-6
                                ? image[Math.round(x0)][Math.round(y0)]
                                : interpolate(Interpolation.Cubic, image, x0, y0)

                        const color = scaleColor(value)
                            .match(/\d+/g)
                            ?.map((c: string) => parseInt(c))

                        const idx = y * (width * 4) + x * 4

                        imageData.data[idx] = color[0]
                        imageData.data[idx + 1] = color[1]
                        imageData.data[idx + 2] = color[2]
                        imageData.data[idx + 3] = 255
                    }

                    const elapsedTime = performance.now() - t0
                    const frameRenderMS = (this.isOnZoom ? FRAME.ZOOM : FRAME.DEFAULT)
                    if (elapsedTime > frameRenderMS) {// if elapsed time > Xms, continue on next frame.
                        ctx?.putImageData(imageData, 0, 0)
                        this.raf = window.requestAnimationFrame(renderFrame)

                        return
                    }
                }
                ctx?.putImageData(imageData, 0, 0)
            }
            this.raf = window.requestAnimationFrame(renderFrame)
        }
    }
}
